/**
 * One-click next step offered under a classified query error, the query-side
 * sibling of `connectionErrorFix.ts`. Where `queryErrorTip` tells the user
 * what to check, this makes the check itself clickable. Only categories with
 * a genuinely useful in-app action get one:
 *
 *  - `object_not_found` → open the tab's database selector ("right query,
 *    wrong database" is the classic cause; field data: consistently a top-2
 *    real error category). Engines whose selector switches the query context
 *    only — Oracle resolves objects by schema, not database, so the selector
 *    would not address the error there.
 *  - `lock_contention` → open the Session Browser to find the blocking
 *    session (engines the Session Browser supports).
 *
 * Labels must stay generic: never echo object names or any other user data.
 */
import type { AnalyticsErrorCategory } from '../../../../core/analytics/analyticsTypes';
import type { QueryMessageAction } from '../../core/types/workspaceTypes';

const SWITCH_DATABASE_ENGINES = new Set(['mssql', 'pgsql', 'mysql']);
const SESSION_BROWSER_ENGINES = new Set(['mssql', 'oracle', 'pgsql', 'mysql']);

export function getQueryErrorAction(
  category: AnalyticsErrorCategory,
  engine: string | undefined
): QueryMessageAction | undefined {
  if (category === 'object_not_found' && engine !== undefined && SWITCH_DATABASE_ENGINES.has(engine)) {
    return { kind: 'switch_database', label: 'Switch database…' };
  }

  if (category === 'lock_contention' && engine !== undefined && SESSION_BROWSER_ENGINES.has(engine)) {
    return { kind: 'open_session_browser', label: 'Find the blocking session' };
  }

  return undefined;
}
